/**
 * /join — Public self-registration page (no event code gate)
 *
 * Stages:
 *   form → otp (code shown + game pick) → thankyou → setup (battle) → waiting → matched
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowUpRight, Check, Clock, Copy, KeyRound, Loader2,
  Radio, RefreshCw, RotateCcw, Swords, Timer, Zap,
} from "lucide-react";

// ── Avatar catalogue ──────────────────────────────────────────────────────────
const AVATAR_OPTIONS = {
  F: [
    { id: "24-girl", label: "Avani", img: "/24-girl.png" },
    { id: "25-girl", label: "Priya", img: "/25-girl.png" },
    { id: "26-girl", label: "Diya",  img: "/26-girl.png" },
    { id: "34-girl", label: "Nisha", img: "/34-girl.png" },
    { id: "35-girl", label: "Sana",  img: "/35-girl.png" },
    { id: "36-girl", label: "Riya",  img: "/36-girl.png" },
    { id: "38-girl", label: "Kavya", img: "/38-girl.png" },
  ],
  M: [
    { id: "27-boy",  label: "Arjun",  img: "/27-boy.png"  },
    { id: "28-boy",  label: "Vikram", img: "/28-boy.png"  },
    { id: "29-boy",  label: "Rohan",  img: "/29-boy.png"  },
    { id: "30-boy",  label: "Kiran",  img: "/30-boy.png"  },
    { id: "31-boy",  label: "Dev",    img: "/31-boy.png"  },
    { id: "32-boy",  label: "Aakash", img: "/32-boy.png"  },
    { id: "33-boy",  label: "Raj",    img: "/33-boy.png"  },
    { id: "37-boy",  label: "Nikhil", img: "/37-boy.png"  },
  ],
};
function getRandomAvatar(g) {
  const pool = AVATAR_OPTIONS[g || "M"];
  return pool[Math.floor(Math.random() * pool.length)];
}

const LANG_OPTIONS = [
  { code: "en",  label: "English"  },
  { code: "hi",  label: "Hindi"    },
  { code: "bho", label: "Bhojpuri" },
];
const OTP_TTL = 60;

// ── OTP digit input row ───────────────────────────────────────────────────────
function OtpRow({ digits, setDigits, refs, length = 5 }) {
  const handleChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const n = [...digits]; n[i] = ch; setDigits(n);
    if (ch && i < length - 1) refs.current[i + 1]?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!txt) return; e.preventDefault();
    const arr = Array(length).fill("");
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setDigits(arr);
    refs.current[Math.min(txt.length, length - 1)]?.focus();
  };
  return (
    <div className="flex gap-2.5 justify-between">
      {digits.map((d, i) => (
        <input key={i} ref={(el) => (refs.current[i] = el)}
          inputMode="numeric" maxLength={1} value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className="otp-dark flex-1" />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function JoinTheBattle() {
  const navigate = useNavigate();

  // stage: form | otp | thankyou | setup | waiting | matched
  const [stage, setStage] = useState("form");

  // ── identity ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [entryCode, setEntryCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  // ── OTP ───────────────────────────────────────────────────────────────────
  const [regOtp, setRegOtp] = useState(Array(5).fill(""));
  const [devOtp, setDevOtp] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(OTP_TTL);
  const otpRefs = useRef([]);
  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── activity choice (picked on OTP screen) ────────────────────────────────
  const [activity, setActivity] = useState(null); // "battle" | "blitz"

  const startCountdown = useCallback(() => {
    setCountdown(OTP_TTL);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
  }, []);

  const sendOtp = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill all fields"); return;
    }
    setSending(true);
    try {
      const r = await api.otpSend(form);
      setDevOtp(r.dev_otp || null);
      if (r.dev_otp) {
        const arr = r.dev_otp.split("").slice(0, 5);
        while (arr.length < 5) arr.push("");
        setRegOtp(arr);
      }
      startCountdown();
      setStage("otp");
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setSending(false); }
  };

  const confirmAndRegister = async () => {
    const code = regOtp.join("");
    if (code.length < 5) { toast.error("Enter the 5-digit code"); return; }
    if (!activity) { toast.error("Please select a game to participate in"); return; }
    setVerifying(true);
    try {
      const r = await api.otpVerify({ ...form, otp: code });
      setEntryCode(r.entry_code || null);
      toast.success(`Welcome, ${form.name.split(" ")[0]}!`);
      if (activity === "blitz") {
        setStage("thankyou");
      } else {
        setStage("thankyou");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP — check the code and try again");
    } finally { setVerifying(false); }
  };

  const resendOtp = async () => {
    if (countdown > 0) return;
    setSending(true);
    try {
      const r = await api.otpSend(form);
      setDevOtp(r.dev_otp || null);
      if (r.dev_otp) { const arr = r.dev_otp.split("").slice(0, 5); while (arr.length < 5) arr.push(""); setRegOtp(arr); }
      startCountdown();
    } catch { toast.error("Failed to resend OTP"); }
    finally { setSending(false); }
  };

  // ── battle setup ──────────────────────────────────────────────────────────
  const [gender, setGender] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [battleMode, setBattleMode] = useState("ai_voice");
  const [lang, setLang] = useState("en");

  // ── registration / polling ────────────────────────────────────────────────
  const [joining, setJoining] = useState(false);
  const [matchInfo, setMatchInfo] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (stage !== "waiting" || !form.phone) return;
    const tick = async () => {
      try {
        const r = await api.tournamentMyMatchStatus(form.phone);
        setMatchInfo((prev) => ({ ...prev, ...r, tournament_live: r.tournament_live ?? prev?.tournament_live ?? false }));
        if (r.status === "admin_started" && r.room_id && r.battle_info) {
          clearInterval(pollRef.current);
          sessionStorage.setItem("proctorly_battle", JSON.stringify({
            player1: r.battle_info.player1,
            player2: r.battle_info.player2,
            room_id: r.room_id,
            role: r.battle_info.role,
            tournament: { id: r.tournament_id, name: r.tournament_name },
            battleMode,
            agentGender: gender === "F" ? "female" : "male",
            p1Lang: lang, p2Lang: lang,
          }));
          setStage("matched");
          setTimeout(() => navigate("/battle"), 1000);
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, form.phone]);

  const register = async () => {
    const avatar = selectedAvatar || getRandomAvatar(gender);
    setJoining(true);
    try {
      const r = await api.tournamentSelfRegister({
        name: form.name, email: form.email, phone: form.phone,
        gender: gender || "", avatar: avatar || {},
        entry_code: entryCode || "",
      });
      setMatchInfo({ ...r, tournament_live: r.tournament_live ?? false });
      if (r.status === "admin_started" && r.battle_info) {
        sessionStorage.setItem("proctorly_battle", JSON.stringify({
          player1: r.battle_info.player1, player2: r.battle_info.player2,
          room_id: r.room_id, role: r.battle_info.role,
          tournament: { id: r.tournament_id, name: r.tournament_name },
          battleMode, agentGender: gender === "F" ? "female" : "male",
          p1Lang: lang, p2Lang: lang,
        }));
        setStage("matched");
        setTimeout(() => navigate("/battle"), 1000);
      } else {
        setStage("waiting");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to register");
    } finally { setJoining(false); }
  };

  const timerColor = countdown > 30 ? "text-emerald-400" : countdown > 10 ? "text-yellow-400" : "text-[color:var(--kl-red)]";
  const timerBg    = countdown > 30 ? "bg-emerald-500/10 border-emerald-500/30" : countdown > 10 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30";

  // ── Cinematic background: vertical column "wall of fighters" ─────────────
  const ALL_BG_AVATARS = [
    "/24-girl.png", "/25-girl.png", "/26-girl.png", "/34-girl.png", "/35-girl.png", "/36-girl.png", "/38-girl.png",
    "/27-boy.png",  "/28-boy.png",  "/29-boy.png",  "/30-boy.png",  "/31-boy.png",  "/32-boy.png",  "/33-boy.png", "/37-boy.png",
    "/fe-1.png",    "/fe-2.png",    "/fe-3.png",    "/fe-4.png",
    "/male-1.png",  "/male-2.png",  "/male-3.png",  "/male-4.png",
  ];
  // 10 vertical columns — edges vivid, center muted (depth parallax)
  const BG_COLS = [
    { dir: "up",   speed: 22, delay: "0s",   opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
    { dir: "down", speed: 29, delay: "2.1s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
    { dir: "up",   speed: 17, delay: "0.5s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
    { dir: "down", speed: 33, delay: "1.1s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
    { dir: "up",   speed: 25, delay: "0s",   opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
    { dir: "down", speed: 20, delay: "1.6s", opacity: 0.26, blur: 3, gs: 38, bri: 0.36 },
    { dir: "up",   speed: 31, delay: "0.3s", opacity: 0.38, blur: 1, gs: 42, bri: 0.42 },
    { dir: "down", speed: 18, delay: "2.6s", opacity: 0.55, blur: 0, gs: 48, bri: 0.48 },
    { dir: "up",   speed: 26, delay: "0.8s", opacity: 0.70, blur: 0, gs: 50, bri: 0.55 },
    { dir: "down", speed: 23, delay: "1.3s", opacity: 0.80, blur: 0, gs: 55, bri: 0.60 },
  ];
  // Each column gets 4 unique avatars (doubled for seamless loop = 8 total)
  const getBgCol = (ci) => {
    const imgs = [];
    for (let i = 0; i < 4; i++) imgs.push(ALL_BG_AVATARS[(ci * 3 + i) % ALL_BG_AVATARS.length]);
    return [...imgs, ...imgs]; // doubled for seamless -50% loop
  };
  const SPARKS = [
    { left: "8%",  delay: "0s",   dx: "12px",  dur: "2.4s" }, { left: "22%", delay: "1.1s", dx: "-9px",  dur: "2.9s" },
    { left: "38%", delay: "0.4s", dx: "16px",  dur: "2.2s" }, { left: "52%", delay: "1.8s", dx: "-11px", dur: "3.1s" },
    { left: "63%", delay: "0.7s", dx: "8px",   dur: "2.6s" }, { left: "76%", delay: "1.4s", dx: "-14px", dur: "2.0s" },
    { left: "88%", delay: "0.2s", dx: "10px",  dur: "2.7s" }, { left: "45%", delay: "2.3s", dx: "-7px",  dur: "2.3s" },
  ];

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: "var(--kl-navy-deep)" }}>
      <Header />

      {/* ── Cinematic "Wall of Fighters" background ──────────────────── */}
      <style>{`
        @keyframes jtb-up    { 0%   { transform: translateY(0); }     100% { transform: translateY(-50%); } }
        @keyframes jtb-down  { 0%   { transform: translateY(-50%); }  100% { transform: translateY(0); } }
        @keyframes jtb-ambi  { 0%,100%{opacity:0.10} 50%{opacity:0.26} }
        @keyframes jtb-ambi2 { 0%,100%{opacity:0.06} 50%{opacity:0.18} }
        @keyframes jtb-glitch{ 0%,95%,100%{opacity:1;transform:translateX(0)}
                               96%{opacity:.7;transform:translateX(-3px)}
                               97%{opacity:.8;transform:translateX(3px)}
                               98%{opacity:1;transform:translateX(0)} }
        @keyframes cl-blink  { 0%,100%{opacity:1} 50%{opacity:0.15} }
      `}</style>

      <div style={{ position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none" }}>

        {/* ── 10 Vertical columns ── */}
        {BG_COLS.map((col, ci) => (
          <div key={ci} style={{
            position: "absolute",
            left: `${ci * 10}%`, top: 0, bottom: 0, width: "10.4%",
            overflow: "hidden",
            opacity: col.opacity,
            filter: col.blur > 0 ? `blur(${col.blur}px)` : undefined,
          }}>
            <div style={{
              display: "flex", flexDirection: "column", gap: 6,
              animation: `${col.dir === "up" ? "jtb-up" : "jtb-down"} ${col.speed}s linear ${col.delay} infinite`,
            }}>
              {getBgCol(ci).map((src, ii) => (
                <img key={ii} src={src} alt="" style={{
                  width: "100%", height: "27vh",
                  objectFit: "cover", objectPosition: "top center",
                  borderRadius: 8, flexShrink: 0,
                  filter: `grayscale(${col.gs}%) brightness(${col.bri}) contrast(1.2)`,
                  // every 4th image in vivid edge columns gets a glitch/highlight animation
                  animation: (ci === 0 || ci === 9) && ii % 3 === 0
                    ? `jtb-glitch ${8 + ci}s ease-in-out ${ii * 1.2}s infinite` : undefined,
                }} />
              ))}
            </div>
          </div>
        ))}

        {/* ── Full-width CRT horizontal scan line sweeping top→bottom ── */}

        {/* ── Side edge fades ── */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(90deg, rgba(6,10,31,0.88) 0%, transparent 16%, transparent 84%, rgba(6,10,31,0.88) 100%)",
        }} />
        {/* ── Top / bottom fades ── */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "linear-gradient(180deg, rgba(6,10,31,0.90) 0%, transparent 14%, transparent 86%, rgba(6,10,31,0.90) 100%)",
        }} />
        {/* ── Centre dark "stage" — form floats here ── */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          background: "radial-gradient(ellipse 48% 80% at 50% 48%, rgba(6,10,31,0.92) 0%, rgba(6,10,31,0.70) 48%, transparent 100%)",
        }} />

        {/* ── Red arena glow (dual — top and bottom) ── */}
        <div style={{
          position: "absolute", top: "-10%", left: "50%", transform: "translateX(-50%)",
          width: 800, height: 500, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(230,57,70,0.14) 0%, transparent 65%)",
          animation: "jtb-ambi 3.5s ease-in-out infinite", zIndex: 3,
        }} />
        <div style={{
          position: "absolute", bottom: "-10%", left: "50%", transform: "translateX(-50%)",
          width: 700, height: 400, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(230,57,70,0.10) 0%, transparent 65%)",
          animation: "jtb-ambi2 4.5s ease-in-out 1s infinite", zIndex: 3,
        }} />

        {/* ── Rising spark particles ── */}
        {SPARKS.map((s, i) => (
          <div key={i} style={{
            position: "absolute", left: s.left, bottom: "5%",
            width: 5, height: 5, borderRadius: "50%",
            background: "var(--kl-red)",
            boxShadow: "0 0 10px rgba(230,57,70,1), 0 0 20px rgba(230,57,70,0.6)",
            "--jtb-dx": s.dx,
            animation: `jtb-spark ${s.dur} ease-out ${s.delay} infinite`,
            zIndex: 4,
          }} />
        ))}

        {/* ── Cyber grid ── */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.035, zIndex: 2,
          backgroundImage: "linear-gradient(rgba(230,57,70,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(230,57,70,0.9) 1px, transparent 1px)",
          backgroundSize: "55px 55px",
        }} />
      </div>

      <div className="relative z-10 px-5 md:px-10 pt-6 pb-10 md:py-10">
        {/* narrow wrapper for form/otp/setup stages */}
        <div className={stage === "thankyou" ? "max-w-3xl mx-auto" : "max-w-xl mx-auto"}>

        {/* ── Page label ───────────────────────────────────────────────── */}
        {stage !== "thankyou" && (
          <>
            <div className="font-mono-kl text-xs uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-2">
              <span className="kl-accent-line mr-3" />Proctorly AI · AI Summit
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.9] tracking-tight mb-6 md:mb-8">
              {stage === "form"    && <>Register <span className="text-[color:var(--kl-red)]">here.</span></>}
              {stage === "otp"     && <>Your <span className="text-[color:var(--kl-red)]">code.</span></>}
              {stage === "setup"   && <>Battle <span className="text-[color:var(--kl-red)]">setup.</span></>}
              {stage === "waiting" && <>You're <span className="text-[color:var(--kl-red)]">in!</span></>}
              {stage === "matched" && <>War room<span className="text-[color:var(--kl-red)]">…</span></>}
            </h1>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: form
        ════════════════════════════════════════════════════════════════ */}
        {stage === "form" && (
          <div className="rounded-xl border border-white/15 p-6 md:p-8 space-y-5" style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>
            <div>
              <label className="form-label">Full Name <span className="req">*</span></label>
              <input className="input-dark" value={form.name}
                onChange={(e) => setF("name", e.target.value)}
                placeholder="Ada Lovelace" autoComplete="name" />
            </div>
            <div>
              <label className="form-label">Email <span className="req">*</span></label>
              <input type="email" className="input-dark" value={form.email}
                onChange={(e) => setF("email", e.target.value)}
                placeholder="you@example.com" autoComplete="email" />
            </div>
            <div>
              <label className="form-label">Phone <span className="req">*</span></label>
              <input type="tel" inputMode="numeric" className="input-dark" value={form.phone}
                onChange={(e) => setF("phone", e.target.value)}
                placeholder="+91 98xxxxxxxx" autoComplete="tel" />
            </div>
            <div>
              <label className="form-label">Gender <span className="req">*</span></label>
              <div className="flex gap-3 mt-1">
                {[{ code: "F", label: "♀ Female" }, { code: "M", label: "♂ Male" }].map(({ code, label }) => (
                  <button key={code} type="button" onClick={() => setGender(code)}
                    className="flex-1 py-3 rounded-lg border font-mono-kl text-sm uppercase tracking-widest transition-all"
                    style={gender === code
                      ? { background: "rgba(230,57,70,0.12)", border: "1.5px solid var(--kl-red)", color: "#fff" }
                      : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.45)" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={sendOtp} disabled={sending || !gender}
              className="btn-submit w-full justify-center disabled:opacity-40 mt-2">
              {sending
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</>
                : <>Get My Code <ArrowUpRight className="w-4 h-4" /></>}
            </button>
            {!gender && (
              <p className="text-white/30 font-mono-kl text-[9px] uppercase tracking-widest text-center">
                Select your gender to continue
              </p>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: otp  —  code card + game selection + confirm
        ════════════════════════════════════════════════════════════════ */}
        {stage === "otp" && (
          <div className="rounded-xl border border-white/10 p-6 md:p-8 space-y-6" style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>

            {/* ── Your registration code ── */}
            <div>
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 mb-3">
                Your registration code — copy &amp; save this!
              </div>
              <div className="rounded-lg border p-5 flex items-center justify-between"
                style={{ background: "rgba(230,57,70,0.08)", borderColor: "rgba(230,57,70,0.4)" }}>
                <div>
                  <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1 flex items-center gap-1.5">
                    <KeyRound className="w-3 h-3" /> Booth Entry Code
                  </div>
                  <div style={{ fontFamily: "var(--kl-display)", fontSize: "clamp(1.8rem, 10vw, 3rem)", letterSpacing: "0.3em", color: "white", wordBreak: "keep-all", lineHeight: 1.1 }}>
                    {devOtp || "·····"}
                  </div>
                  <div className="font-mono-kl text-[9px] text-white/35 mt-2 uppercase tracking-widest">
                    Show this at the Proctorly booth to sign in anytime
                  </div>
                </div>
                {devOtp && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(devOtp).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl shrink-0">
                    {codeCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    {codeCopied ? "Copied!" : "Copy"}
                  </button>
                )}
              </div>
              {/* Manual entry if SMS sent (devOtp won't be available in prod) */}
              {!devOtp && (
                <div className="mt-4 space-y-3">
                  <p className="text-white/50 text-sm">Enter the code sent to <span className="text-white">{form.phone}</span></p>
                  <OtpRow digits={regOtp} setDigits={setRegOtp} refs={otpRefs} />
                  {countdown === 0 && (
                    <button onClick={resendOtp} disabled={sending}
                      className="flex items-center gap-1.5 text-[color:var(--kl-red)] text-xs font-mono-kl uppercase tracking-widest hover:opacity-80">
                      <RefreshCw className="w-3 h-3" /> Resend
                    </button>
                  )}
                </div>
              )}
              {devOtp && countdown > 0 && (
                <div className={`mt-2 inline-flex items-center gap-1.5 text-xs font-mono-kl px-2 py-1 rounded border ${timerBg} ${timerColor}`}>
                  {countdown}s
                </div>
              )}
            </div>

            {/* ── Game selection ── */}
            <div>
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 mb-3">
                What would you like to play?
              </div>
              <div className="grid gap-3">
                <button onClick={() => setActivity("battle")}
                  className="w-full rounded-xl border p-4 text-left flex items-center gap-4 transition-all"
                  style={activity === "battle"
                    ? { background: "rgba(230,57,70,0.10)", border: "1.5px solid var(--kl-red)" }
                    : { background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: activity === "battle" ? "rgba(230,57,70,0.2)" : "rgba(255,255,255,0.05)", border: activity === "battle" ? "1.5px solid var(--kl-red)" : "1px solid rgba(255,255,255,0.1)" }}>
                    <Swords className="w-5 h-5" style={{ color: activity === "battle" ? "var(--kl-red)" : "rgba(255,255,255,0.4)" }} />
                  </div>
                  <div>
                    <div className="font-display text-lg uppercase text-white">⚔️ Head-to-Head Battle</div>
                    <div className="text-white/40 text-xs mt-0.5">Tournament · AI face-off · 10 rounds</div>
                  </div>
                  {activity === "battle" && <Check className="w-5 h-5 text-[color:var(--kl-red)] ml-auto shrink-0" />}
                </button>

                <button onClick={() => setActivity("blitz")}
                  className="w-full rounded-xl border p-4 text-left flex items-center gap-4 transition-all"
                  style={activity === "blitz"
                    ? { background: "rgba(255,209,102,0.08)", border: "1.5px solid rgba(255,209,102,0.6)" }
                    : { background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: activity === "blitz" ? "rgba(255,209,102,0.15)" : "rgba(255,255,255,0.05)", border: activity === "blitz" ? "1px solid rgba(255,209,102,0.5)" : "1px solid rgba(255,255,255,0.1)" }}>
                    <Zap className="w-5 h-5" style={{ color: activity === "blitz" ? "var(--kl-yellow)" : "rgba(255,255,255,0.4)" }} />
                  </div>
                  <div>
                    <div className="font-display text-lg uppercase text-white">⚡ 60-Second Blitz</div>
                    <div className="text-white/40 text-xs mt-0.5">Solo · AI question · Skill badge</div>
                  </div>
                  {activity === "blitz" && <Check className="w-5 h-5 text-[color:var(--kl-yellow)] ml-auto shrink-0" />}
                </button>
              </div>
            </div>

            {/* ── Confirm button ── */}
            <div className="flex items-center justify-between pt-1">
              <button onClick={() => { setStage("form"); setRegOtp(Array(5).fill("")); setActivity(null); }}
                className="btn-ghost-dark text-xs">← Back</button>
              <button onClick={confirmAndRegister}
                disabled={verifying || (!devOtp && regOtp.join("").length < 5) || !activity}
                className="btn-submit disabled:opacity-40">
                {verifying
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Confirming…</>
                  : <>Confirm Registration <Check className="w-4 h-4" /></>}
              </button>
            </div>
            {!activity && (
              <p className="text-white/30 font-mono-kl text-[9px] uppercase tracking-widest text-center -mt-3">
                Select a game above to continue
              </p>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: thankyou
        ════════════════════════════════════════════════════════════════ */}
        {stage === "thankyou" && (
          <div className="relative rounded-2xl border border-[color:var(--kl-red)]/50 overflow-hidden"
            style={{ background: "rgba(6,10,31,0.82)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)", boxShadow: "0 0 60px rgba(230,57,70,0.2), 0 0 120px rgba(230,57,70,0.08)" }}>

            {/* Corner glow */}
            <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-25 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(230,57,70,0.9), transparent 60%)" }} />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl opacity-15 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(230,57,70,0.7), transparent 60%)" }} />

            <div className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row items-stretch gap-10">
              {/* Avatar — tall portrait */}
              <div className="shrink-0 w-48 md:w-64" style={{ minHeight: 340 }}>
                <div className="w-full h-full rounded-xl overflow-hidden border-2 border-[color:var(--kl-red)]/60"
                  style={{ boxShadow: "0 0 40px rgba(230,57,70,0.4)", minHeight: 340 }}>
                  <img
                    src={selectedAvatar?.img || "/veebee.png"}
                    alt="Your avatar"
                    className="w-full h-full object-cover object-top"
                    style={{ minHeight: 340 }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 flex flex-col justify-between gap-6">
                <div>
                  <div className="font-mono-kl text-[10px] uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[color:var(--kl-red)] inline-block" style={{ animation: "cl-blink 1s ease-in-out infinite" }} />
                    Registration Confirmed
                  </div>
                  <h2 className="font-display uppercase leading-none text-white"
                    style={{ fontSize: "clamp(1.8rem, 3.8vw, 3.2rem)", letterSpacing: "-0.01em", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                    Welcome,&nbsp;<span className="text-[color:var(--kl-red)]">{form.name.split(" ")[0]}!</span>
                  </h2>
                  <p className="text-white/50 text-sm mt-4 leading-relaxed">
                    You're registered for&nbsp;
                    <strong className="text-white">{activity === "battle" ? "⚔️ Head-to-Head Battle" : "⚡ 60-Second Blitz"}</strong>
                    &nbsp;at the AI Summit. Head to the Proctorly booth and show your booth code below.
                  </p>
                </div>

                {/* Booth entry code */}
                {entryCode && (
                  <div className="rounded-xl border p-5"
                    style={{ background: "rgba(230,57,70,0.07)", borderColor: "rgba(230,57,70,0.30)" }}>
                    <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-2 flex items-center gap-1.5">
                      <KeyRound className="w-3 h-3" /> Your Booth Entry Code
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="font-display text-5xl tracking-[0.4em] text-white">{entryCode}</span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(entryCode).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }); }}
                        className="flex items-center gap-1.5 text-white/50 hover:text-white transition font-mono-kl text-[9px] uppercase tracking-widest">
                        {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                        {codeCopied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                    <p className="text-white/30 text-[9px] font-mono-kl mt-1.5 uppercase tracking-widest">
                      Show this at the Proctorly booth to enter
                    </p>
                  </div>
                )}

                {/* CTA */}
                <div className="flex flex-wrap gap-3">
                  {activity === "battle" ? (
                    <button onClick={() => setStage("setup")} className="btn-submit">
                      Set Up My Battle <Swords className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={() => navigate("/blitz", { state: { player: { name: form.name, email: form.email, phone: form.phone }, verified: true } })} className="btn-submit"
                      style={{ background: "rgba(255,209,102,0.15)", borderColor: "rgba(255,209,102,0.5)", color: "#fff" }}>
                      Start My Blitz <Zap className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: setup  —  avatar + mode + language
        ════════════════════════════════════════════════════════════════ */}
        {stage === "setup" && (
          <div className="rounded-xl border border-white/10 p-6 md:p-8 space-y-6" style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>
            <div className="flex items-center justify-between -mt-2">
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]">
                Battle Setup — {form.name.split(" ")[0]}
              </div>
              <button onClick={() => setStage("thankyou")}
                className="font-mono-kl text-[10px] uppercase tracking-widest text-white/30 hover:text-white transition">
                ← Back
              </button>
            </div>

            {/* Avatar picker (gender already picked on form) */}
            <div>
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 mb-3">Choose Your Avatar</div>
              <div className="grid grid-cols-4 gap-2">
                {(AVATAR_OPTIONS[gender] || AVATAR_OPTIONS.M).map((av) => (
                  <button key={av.id} onClick={() => setSelectedAvatar(av)}
                    className="relative rounded-lg overflow-hidden border-2 transition-all aspect-[3/4]"
                    style={selectedAvatar?.id === av.id
                      ? { borderColor: "var(--kl-red)", boxShadow: "0 0 14px rgba(230,57,70,0.5)" }
                      : { borderColor: "rgba(255,255,255,0.08)" }}>
                    <img src={av.img} alt={av.label} className="w-full h-full object-cover object-top" />
                    {selectedAvatar?.id === av.id && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[color:var(--kl-red)] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 text-center font-mono-kl text-[8px] uppercase tracking-widest py-1 text-white/60"
                      style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.7))" }}>
                      {av.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Mode */}
            <div>
              <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 mb-2">AI Agent Mode</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { code: "live_avatar", label: "🎥 Live Avatar", sub: "Real-time AI · EN only" },
                  { code: "ai_voice",    label: "🎙 AI Voice",    sub: "Hindi · Bhojpuri · English" },
                ].map(({ code, label, sub }) => (
                  <button key={code} onClick={() => { setBattleMode(code); if (code === "live_avatar") setLang("en"); }}
                    className="text-left px-4 py-3 rounded-lg border transition-all"
                    style={battleMode === code
                      ? { background: "rgba(230,57,70,0.12)", border: "1.5px solid var(--kl-red)", color: "#fff" }
                      : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>
                    <div className="font-display text-sm uppercase">{label}</div>
                    <div className="font-mono-kl text-[9px] uppercase tracking-widest opacity-60 mt-0.5">{sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            {battleMode === "ai_voice" && (
              <div>
                <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/40 mb-2">Language</div>
                <div className="flex gap-3">
                  {LANG_OPTIONS.map(({ code, label }) => (
                    <button key={code} onClick={() => setLang(code)}
                      className="flex-1 py-2.5 rounded-lg border font-mono-kl text-xs uppercase tracking-widest transition-all"
                      style={lang === code
                        ? { background: "rgba(230,57,70,0.12)", border: "1.5px solid var(--kl-red)", color: "#fff" }
                        : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.45)" }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button onClick={register} disabled={joining}
              className="btn-submit w-full justify-center disabled:opacity-40">
              {joining
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Registering…</>
                : <>Register for Battle <Swords className="w-4 h-4 ml-1" /></>}
            </button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: waiting
        ════════════════════════════════════════════════════════════════ */}
        {stage === "waiting" && (
          <div className="rounded-xl border border-white/10 p-6 md:p-8 space-y-5" style={{ background: "rgba(6,10,31,0.75)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <span className="font-mono-kl text-[10px] uppercase tracking-widest text-yellow-400">Registered</span>
            </div>
            <div className="font-display text-3xl uppercase text-white">{form.name}</div>

            {/* Booth code */}
            {entryCode && (
              <div className="rounded-lg border p-4 flex items-center justify-between"
                style={{ background: "rgba(230,57,70,0.08)", borderColor: "rgba(230,57,70,0.35)" }}>
                <div>
                  <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1 flex items-center gap-1.5">
                    <KeyRound className="w-3 h-3" /> Show this at the Proctorly booth
                  </div>
                  <div style={{ fontFamily: "var(--kl-display)", fontSize: "clamp(1.6rem, 9vw, 2.5rem)", letterSpacing: "0.3em", color: "white", lineHeight: 1.1 }}>{entryCode}</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(entryCode).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }); }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl">
                  {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  {codeCopied ? "Copied" : "Copy"}
                </button>
              </div>
            )}

            {/* Match info */}
            {matchInfo?.match_num ? (
              <div className="rounded-xl border overflow-hidden"
                style={{ borderColor: "rgba(230,57,70,0.35)", background: "rgba(230,57,70,0.06)" }}>

                {/* Tournament name header */}
                <div className="px-5 py-3 border-b flex items-center gap-2"
                  style={{ borderColor: "rgba(230,57,70,0.25)", background: "rgba(230,57,70,0.10)" }}>
                  <span style={{ fontSize: 16 }}>🏆</span>
                  <span className="font-display text-base md:text-lg uppercase tracking-wide text-white">
                    {matchInfo.tournament_name || "Your Tournament"}
                  </span>
                </div>

                <div className="p-5 flex flex-col md:flex-row items-center gap-6">
                  {/* Match number */}
                  <div className="text-center shrink-0">
                    <div className="font-mono-kl text-[9px] uppercase tracking-widest text-white/40 mb-1">Your Match</div>
                    <div className="font-display text-6xl md:text-7xl text-white leading-none">
                      #{matchInfo.match_num}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="hidden md:block w-px self-stretch" style={{ background: "rgba(255,255,255,0.12)" }} />
                  <div className="block md:hidden h-px w-full" style={{ background: "rgba(255,255,255,0.12)" }} />

                  {/* Opponent */}
                  {matchInfo.opponent_name ? (
                    <div className="flex-1 text-center md:text-left">
                      <div className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)] mb-2 flex items-center gap-2 justify-center md:justify-start">
                        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--kl-red)] inline-block" style={{ animation: "cl-blink 1s ease-in-out infinite" }} />
                        Your Opponent
                      </div>
                      <div className="font-display text-3xl md:text-4xl uppercase text-white leading-none">
                        {matchInfo.opponent_name}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono-kl uppercase tracking-widest"
                        style={{ background: "rgba(230,57,70,0.15)", color: "rgba(230,57,70,0.9)", border: "1px solid rgba(230,57,70,0.3)" }}>
                        <Swords className="w-3 h-3" /> Paired — awaiting admin start
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 text-center md:text-left">
                      <div className="font-mono-kl text-[10px] uppercase tracking-widest text-white/35 mb-2">Waiting for opponent</div>
                      <div className="font-display text-2xl md:text-3xl uppercase leading-none"
                        style={{ color: "rgba(255,255,255,0.25)" }}>
                        ??? Challenger
                      </div>
                      <div className="flex items-center gap-2 mt-3 justify-center md:justify-start">
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />
                        <span className="font-mono-kl text-[9px] uppercase tracking-widest text-white/30">Admin will assign your opponent…</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* No match assigned yet — just registered, waiting for admin */
              <div className="rounded-xl border border-yellow-400/25 p-5 flex items-start gap-4"
                style={{ background: "rgba(250,204,21,0.05)" }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "rgba(250,204,21,0.15)" }}>
                  <Clock className="w-4 h-4 text-yellow-400" />
                </div>
                <div>
                  <div className="font-mono-kl text-[10px] uppercase tracking-widest text-yellow-400 mb-1">You're Registered!</div>
                  <p className="text-white/75 text-sm leading-relaxed">
                    Head to the <strong className="text-white">Proctorly booth</strong> and show your code.
                    Admin will assign you to a tournament match — you'll be notified here automatically.
                  </p>
                </div>
              </div>
            )}

            {/* Tournament LIVE banner */}
            {matchInfo?.tournament_live && matchInfo?.opponent_name ? (
              <div className="rounded-md border border-[color:var(--kl-red)]/60 p-4"
                style={{ background: "rgba(230,57,70,0.12)" }}>
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1 flex items-center gap-1.5">
                  <Radio className="w-3 h-3 animate-pulse" /> 🔴 Tournament is LIVE!
                </div>
                <p className="text-white/85 text-sm font-medium">
                  Come to the <strong>Kiba Labs booth</strong> right now — your match will be called shortly!
                </p>
              </div>
            ) : matchInfo?.opponent_name ? (
              <div className="rounded-md border border-yellow-400/20 bg-yellow-400/5 p-4">
                <div className="font-mono-kl text-[9px] uppercase tracking-widest text-yellow-400 mb-1">Paired! Waiting for admin</div>
                <p className="text-white/65 text-sm">
                  Keep a watch on the <strong className="text-white">Proctorly booth</strong> for your slot time.
                </p>
              </div>
            ) : null}

            <div className="flex items-center gap-2 text-white/30 font-mono-kl text-[9px]">
              <Loader2 className="w-3 h-3 animate-spin shrink-0" />
              Auto-updates every 1.5s
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            STAGE: matched
        ════════════════════════════════════════════════════════════════ */}
        {stage === "matched" && (
          <div className="rounded-xl border border-[color:var(--kl-red)]/40 p-8 text-center space-y-4"
            style={{ background: "rgba(230,57,70,0.07)" }}>
            <div className="font-mono-kl text-[11px] uppercase tracking-widest text-[color:var(--kl-red)]">Match started!</div>
            <div className="font-display text-5xl uppercase">
              Entering<br /><span className="text-[color:var(--kl-red)]">war room…</span>
            </div>
            <Loader2 className="w-7 h-7 animate-spin mx-auto text-white/70" />
          </div>
        )}

        {/* Blitz upsell while waiting */}
        {stage === "waiting" && (
          <div className="mt-5 rounded-md border border-[color:var(--kl-red)]/40 p-5 flex flex-col md:flex-row items-start md:items-center gap-4"
            style={{ background: "linear-gradient(135deg, rgba(230,57,70,0.10), rgba(255,209,102,0.04))" }}>
            <div className="w-12 h-12 rounded-md flex items-center justify-center bg-[color:var(--kl-red)] text-white shrink-0">
              <Timer className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-mono-kl text-[10px] uppercase tracking-[0.25em] text-[color:var(--kl-yellow)]">While you wait</div>
              <div className="font-display text-xl uppercase tracking-tight text-white mt-1">Boost your score — take the 60-second Blitz</div>
              <p className="text-white/55 text-sm mt-1">One AI question. 60 seconds. Win a skill badge.</p>
            </div>
            <button onClick={() => navigate("/blitz", { state: { player: { name: form.name, email: form.email, phone: form.phone }, verified: true } })} className="btn-submit shrink-0">
              Take Blitz <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {stage !== "thankyou" && (
          <div className="mt-8 text-center text-white/30 font-mono-kl text-[10px] uppercase tracking-widest">
            Proctorly AI · Powered by Kiba Labs · AI Summit
          </div>
        )}
        </div>{/* end narrow/wide wrapper */}
      </div>
    </div>
  );
}
